import { Plus, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';

import { bannerItemIsEmpty, emptyBannerItem } from '../../api/mappers';
import type { RailFormInput } from '../../schemas/block-form-schema';
import { BannerItemFields } from '../slides/banner-item-fields';

export interface RailHeaderFieldsProps {
  storeId: string | null;
}

/**
 * Optional banner-shaped header. Collapsed to one "Add a header" button while empty; removing
 * the header clears every field so the mapper sends `null`.
 */
export function RailHeaderFields({ storeId }: RailHeaderFieldsProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<RailFormInput>();
  const panelId = useId();
  const [open, setOpen] = useState(() => !bannerItemIsEmpty(form.getValues('header')));

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full border-dashed"
        aria-expanded={false}
        aria-controls={panelId}
        onClick={() => setOpen(true)}
      >
        <Plus aria-hidden />
        {t('rail.addHeader')}
      </Button>
    );
  }

  return (
    <div id={panelId} className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            form.setValue('header', emptyBannerItem(), { shouldDirty: true, shouldValidate: true });
            setOpen(false);
          }}
        >
          <X aria-hidden />
          {t('rail.removeHeader')}
        </Button>
      </div>
      <BannerItemFields name="header" storeId={storeId} />
    </div>
  );
}
